import { useParams, Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";

const siteUrl = import.meta.env.VITE_SITE_URL || "https://gtbuy.garden";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Header />
        <main className="min-h-screen pt-24 pb-16">
          <div className="max-w-[720px] mx-auto px-4 md:px-6 text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">Article Not Found</h1>
            <Link
              to="/"
              className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": "https://readdy.ai/api/search-image?query=Clean%20minimalist%20workspace%20flatlay%20with%20laptop%20showing%20spreadsheet%20data%2C%20soft%20natural%20lighting%2C%20warm%20neutral%20aesthetic%2C%20white%20desk%2C%20editorial%20tech%20lifestyle%20photography%2C%20modern%20cozy%20home%20office%2C%20gentle%20morning%20atmosphere&width=1200&height=630&seq=blog-card-01&orientation=landscape",
    "datePublished": "2026-07-01",
    "dateModified": "2026-07-01",
    "author": {
      "@type": "Organization",
      "name": "GTBuy Garden",
      "url": siteUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "GTBuy Garden",
      "url": siteUrl,
      "logo": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/54d75be42d5913aa14ca64dd22e5d1e3.jpeg"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": siteUrl + "/article/" + article.slug
    },
    "inLanguage": "en"
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <Header />
      <main className="min-h-screen">
        <article>
          <header className="pt-28 pb-12 md:pt-32 md:pb-16 bg-background-50">
            <div className="max-w-[720px] mx-auto px-4 md:px-6">
              <div className="flex items-center gap-3 text-xs text-foreground-400 mb-4">
                <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">
                  Home
                </Link>
                <span>/</span>
                <span className="text-primary-500">{article.category}</span>
              </div>
              <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime}</span>
              </div>
            </div>
          </header>

          <div className="max-w-[720px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="rounded-2xl overflow-hidden mb-12">
              <img
                src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20workspace%20flatlay%20with%20laptop%20showing%20spreadsheet%20data%2C%20soft%20natural%20lighting%2C%20warm%20neutral%20aesthetic%2C%20white%20desk%2C%20editorial%20tech%20lifestyle%20photography%2C%20modern%20cozy%20home%20office%2C%20gentle%20morning%20atmosphere&width=1200&height=630&seq=blog-card-01&orientation=landscape"
                alt={article.title}
                className="w-full h-auto"
              />
            </div>

            <div className="prose-custom">
              {article.content.map((block, index) => {
                if (block.type === "heading") {
                  return (
                    <h2 key={index} className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4 leading-snug">
                      {block.text}
                    </h2>
                  );
                }
                return (
                  <p key={index} className="text-foreground-700 text-base md:text-lg leading-relaxed mb-5">
                    {block.text}
                  </p>
                );
              })}
            </div>

            <div className="mt-14 pt-10 border-t border-background-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Link
                  to="/"
                  className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-semibold bg-background-100 text-foreground-800 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-arrow-left-line mr-1.5"></i>
                  Back to Home
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center px-6 py-2.5 rounded-full text-sm font-semibold bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Try GTBuy Sheet
                  <i className="ri-arrow-right-up-line ml-1.5"></i>
                </a>
              </div>
            </div>

            <div className="mt-12 p-6 md:p-8 bg-background-100 rounded-2xl">
              <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">
                Related Reading
              </h3>
              <p className="text-sm text-foreground-500 mb-4">
                Want to learn more about spreadsheet-based product comparison? Check out these resources:
              </p>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/"
                    className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    GTBuy Garden Homepage — Browse all categories and start comparing
                  </Link>
                </li>
                <li>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    Main Store — Visit kaspreadsheet.com for the full product catalog
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}